/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type RateSessionInput } from '@therapy/shared-validators';

import { apiClient } from './client.js';

import type { Page } from './resources.js';

export type { Page, RateSessionInput };

export type SessionStatus =
  | 'scheduled'
  | 'in_progress'
  | 'completed'
  | 'cancelled'
  | 'no_show'
  | 'client_late'
  | 'consultant_attended_client_absent';

/** Query params for the paginated session list (offset/limit + search + status + day). */
export interface SessionsPageParams {
  readonly limit: number;
  readonly offset: number;
  readonly q?: string | undefined;
  readonly status?: SessionStatus | undefined;
  readonly date?: string | undefined;
}

export interface Session {
  readonly id: string;
  readonly clientId: string;
  readonly therapistId: string | null;
  readonly itpId: string | null;
  readonly sessionType: string | null;
  readonly location: string | null;
  readonly roomId: string | null;
  readonly status: SessionStatus;
  readonly scheduledStart: string;
  readonly scheduledEnd: string | null;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
  readonly cancelledAt: string | null;
  readonly cancelReason: string | null;
  readonly statusReason: string | null;
  readonly consultantCheckedInAt: string | null;
  readonly engagementRating: number | null;
  readonly cooperationRating: number | null;
  readonly ratingNote: string | null;
}

export interface SoapBody {
  readonly subjective: string;
  readonly objective: string;
  readonly assessment: string;
  readonly plan: string;
}

export interface Trial {
  readonly id: string;
  readonly goalId: string;
  readonly correct: number;
  readonly incorrect: number;
  readonly prompted: number;
  readonly note: string | null;
  readonly recordedAt: string;
}

export interface SessionNote {
  readonly id: string;
  readonly sessionId: string;
  readonly clientId: string;
  readonly soap: SoapBody;
  readonly status: 'draft' | 'signed';
  readonly signedAt: string | null;
  readonly signedBy: string | null;
  readonly trials: readonly Trial[];
}

export interface TrialInput {
  readonly goalId: string;
  readonly correct: number;
  readonly incorrect: number;
  readonly prompted: number;
  readonly note?: string | undefined;
}

export interface SaveNotePayload {
  readonly soap: SoapBody;
  readonly trials?: readonly TrialInput[];
}

export interface CreateSessionPayload {
  readonly clientId: string;
  readonly therapistId?: string;
  readonly sessionType?: string;
  readonly location?: string;
  /** ISO-8601 instant. */
  readonly scheduledStart: string;
}

export const sessionsApi = {
  async list(): Promise<readonly Session[]> {
    const { data } = await apiClient.get<readonly Session[]>('/sessions');
    return data;
  },
  async listPage(params: SessionsPageParams): Promise<Page<Session>> {
    const { data } = await apiClient.get<Page<Session>>('/sessions/page', { params });
    return data;
  },
  async create(payload: CreateSessionPayload): Promise<Session> {
    const { data } = await apiClient.post<Session>('/sessions', payload);
    return data;
  },
  async getOne(id: string): Promise<Session> {
    const { data } = await apiClient.get<Session>(`/sessions/${id}`);
    return data;
  },
  async transition(id: string, action: 'start' | 'complete' | 'no-show'): Promise<Session> {
    const { data } = await apiClient.post<Session>(`/sessions/${id}/${action}`);
    return data;
  },
  async cancel(id: string, reason?: string): Promise<Session> {
    const { data } = await apiClient.post<Session>(`/sessions/${id}/cancel`, { reason });
    return data;
  },
  async clientLate(id: string, reason?: string): Promise<Session> {
    const { data } = await apiClient.post<Session>(`/sessions/${id}/client-late`, { reason });
    return data;
  },
  async consultantCheckIn(id: string): Promise<Session> {
    const { data } = await apiClient.post<Session>(`/sessions/${id}/consultant-check-in`);
    return data;
  },
  async consultantAbsent(id: string, reason: string): Promise<Session> {
    const { data } = await apiClient.post<Session>(`/sessions/${id}/consultant-absent`, { reason });
    return data;
  },
  async rate(id: string, input: RateSessionInput): Promise<Session> {
    const { data } = await apiClient.patch<Session>(`/sessions/${id}/rating`, input);
    return data;
  },
  async reschedule(id: string, scheduledStart: string): Promise<Session> {
    const { data } = await apiClient.patch<Session>(`/sessions/${id}/reschedule`, {
      scheduledStart,
    });
    return data;
  },
  async getNote(id: string): Promise<SessionNote | null> {
    try {
      const { data } = await apiClient.get<SessionNote>(`/sessions/${id}/note`);
      return data;
    } catch (error) {
      if (isNotFound(error)) {
        return null;
      }
      throw error;
    }
  },
  async saveNote(id: string, payload: SaveNotePayload): Promise<SessionNote> {
    const { data } = await apiClient.put<SessionNote>(`/sessions/${id}/note`, payload);
    return data;
  },
  async signNote(id: string): Promise<SessionNote> {
    const { data } = await apiClient.post<SessionNote>(`/sessions/${id}/note/sign`);
    return data;
  },
};

/** A 404 from `getNote` means "no note yet", which is a normal empty state. */
function isNotFound(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    (error as { response?: { status?: number } }).response?.status === 404
  );
}
